<template>
    <div class="create-template-page">
        <!-- Progress Steps -->
        <div class="steps-header">
            <a-steps :current="currentStep" size="small">
                <a-step title="Set up template" :status="currentStep > 0 ? 'finish' : 'process'" />
                <a-step
                    title="Edit template"
                    :status="currentStep > 1 ? 'finish' : currentStep === 1 ? 'process' : 'wait'"
                />
                <a-step title="Submit for Review" :status="currentStep === 2 ? 'process' : 'wait'" />
            </a-steps>
        </div>

        <div class="main-content">
            <div class="content-wrapper">
                <!-- Main Form Area -->
                <div class="form-area">
                    <!-- Step 0: Set up template -->
                    <TemplateSetup
                        v-if="currentStep === 0"
                        :form="form"
                        :templateTypes="getTemplateTypes"
                        :catalogueSubtypes="templateTypes.CATALOGUE_SUBTYPES"
                        :authenticationSubtypes="templateTypes.AUTHENTICATION_SUBTYPES"
                        @update-category="updateCategory"
                        @update-template-type="updateTemplateType"
                        @update-catalogue-subtype="updateCatalogueSubType"
                        @update-auth-subtype="updateAuthSubType"
                    />

                    <!-- Step 1: Edit template -->
                    <div v-if="currentStep === 1" class="step-content">
                        <div class="template-header-info">
                            <span class="material-icons" :class="getCategoryIcon">{{ getCategoryIconName }}</span>
                            <div class="info">
                                <span class="template-name">{{ form.name || "your_template_name" }} • {{ getLanguageLabel }}</span>
                                <span class="template-meta">{{ form.category }} • {{ getTemplateTypeLabel }}</span>
                            </div>
                        </div>

                        <div class="section-card">
                            <h4>Template name and language</h4>
                            <div v-if="editMode" class="edit-mode-notice" style="margin-bottom: 16px; padding: 12px; background: #fff7e6; border: 1px solid #ffd591; border-radius: 4px; color: #ad6800; font-size: 13px;">
                                <span class="material-icons" style="font-size: 16px; vertical-align: middle; margin-right: 4px;">info</span>
                                <span>Template name and language cannot be changed when editing. You can only edit category (if not approved), components, and time-to-live.</span>
                            </div>
                            <div class="form-row">
                                <div class="form-group" style="flex: 2">
                                    <label>Name your template</label>
                                    <a-input
                                        v-model="form.name"
                                        :disabled="editMode"
                                        placeholder="Enter a template name"
                                        :maxLength="512"
                                        @input="validateName"
                                    />
                                    <span class="char-count">{{ (form.name || "").length }}/512</span>
                                    <span class="error-text" v-if="errorInfo.name">{{ errorInfo.name }}</span>
                                </div>
                                <div class="form-group" style="flex: 1">
                                    <label>Select language</label>
                                    <a-select 
                                        v-model="form.language" 
                                        :disabled="editMode"
                                        style="width: 100%" 
                                        showSearch
                                    >
                                        <a-select-option v-for="lang of languages" :key="lang.code" :value="lang.code">
                                            {{ lang.label }}
                                        </a-select-option>
                                    </a-select>
                                </div>
                            </div>
                        </div>

                        <!-- Carousel Content (no header/footer allowed) -->
                        <div v-if="form.templateType === 'CAROUSEL'" class="section-card">
                            <h4>Content</h4>
                            <p class="description">
                                Create two separate messages: First, a general text message (body). Second, carousel cards with images/videos. Each carousel card can have its own body text (optional) and buttons (required, at least 1). The general body message cannot have buttons - only carousel cards can have buttons.
                            </p>
                            
                            <BodyEditor
                                :body="form.body"
                                :variables="bodyVariables"
                                :errorMessage="errorInfo.body"
                                :rows="4"
                                :disableFormatting="true"
                                @update:body="form.body = $event"
                                @add-variable="addVariable"
                                @update-variable-example="updateBodyVariableExample"
                            />
                            
                            <CarouselEditor
                                :cards="form.carouselCards"
                                @add-card="addCarouselCard"
                                @remove-card="removeCarouselCard"
                                @move-card-up="moveCarouselCardUp"
                                @move-card-down="moveCarouselCardDown"
                                @update-card-body="updateCarouselCardBody"
                                @add-card-variable="addCarouselCardVariable"
                                @update-card-variable-example="updateCarouselCardVariableExample"
                                @add-card-button="addCarouselCardButton"
                                @remove-card-button="removeCarouselCardButton"
                                @card-file-upload="handleCarouselCardFileUpload"
                                @remove-card-media="removeCarouselCardMedia"
                            />
                        </div>

                        <!-- Default Content -->
                        <div v-else class="section-card">
                            <h4>Content</h4>
                            <p class="description">
                                {{ getContentDescription }}
                            </p>

                            <!-- Header (Product Message için gizli - otomatik ekleniyor) -->
                            <HeaderEditor
                                v-if="!hasProductButton"
                                :headerFormat="form.headerFormat"
                                :headerText="form.headerText"
                                :headerVariables="headerVariables"
                                :media="media"
                                :uploading="uploading"
                                :exampleMedia="form.exampleMedia"
                                :isProductHeader="hasProductButton"
                                :templateType="form.templateType"
                                @update:headerFormat="form.headerFormat = $event"
                                @update:headerText="handleHeaderTextUpdate"
                                @update-header-variable-example="updateHeaderVariableExample"
                                @file-upload="handleFileUpload"
                                @remove-media="removeMedia"
                            />
                            
                            <!-- Product Header Info -->
                            <div v-if="hasProductButton" class="product-header-auto-info">
                                <a-icon type="info-circle" style="color: #1890ff;" />
                                <span>Product header is automatically added from your catalogue. No manual configuration needed.</span>
                            </div>

                            <!-- Limited Time Offer -->
                            <LimitedTimeOfferEditor
                                v-if="form.templateType === 'LIMITED_TIME_OFFER'"
                                :data="form.limitedTimeOffer"
                                @update="form.limitedTimeOffer = $event"
                            />

                            <!-- Body -->
                            <BodyEditor
                                :body="form.body"
                                :variables="bodyVariables"
                                :errorMessage="errorInfo.body"
                                @update:body="form.body = $event"
                                @insert-format="insertFormat"
                                @add-variable="addVariable"
                                @update-variable-example="updateBodyVariableExample"
                            />
                        </div>

                        <!-- Footer Section (not supported for LIMITED_TIME_OFFER and CAROUSEL) -->
                        <div v-if="form.templateType !== 'LIMITED_TIME_OFFER' && form.templateType !== 'CAROUSEL'" class="section-card">
                            <div class="form-group">
                                <label>Footer • Optional</label>
                                <a-input v-model="form.footer" placeholder="Enter text" :maxLength="60" />
                                <span class="char-count">{{ (form.footer || "").length }}/60</span>
                            </div>
                        </div>

                        <!-- Authentication Settings Section -->
                        <div v-if="form.category === 'AUTHENTICATION'" class="section-card">
                            <AuthenticationEditor
                                :data="form.authentication"
                                @update="form.authentication = $event"
                            />
                        </div>

                        <!-- TTL Settings Section (Global for all categories) -->
                        <div class="section-card">
                            <TTLEditor
                                :value="form.messageSendTtlSeconds"
                                :category="form.category"
                                @update="form.messageSendTtlSeconds = $event"
                            />
                        </div>

                        <!-- Buttons -->
                        <div v-if="form.templateType !== 'CAROUSEL'" class="section-card">
                            <ButtonEditor
                                :buttons="form.buttons"
                                :availableButtonTypes="availableButtonTypes"
                                :templateType="form.templateType"
                                :category="form.category"
                                @add-button="handleAddButton"
                                @remove-button="handleRemoveButton"
                            />
                        </div>
                    </div>

                    <!-- Step 2: Submit for Review -->
                    <div v-if="currentStep === 2" class="step-content">
                        <!-- Inline Error Alert -->
                        <a-alert
                            v-if="submitError"
                            type="error"
                            show-icon
                            closable
                            class="submit-error-alert"
                            @close="submitError = null"
                        >
                            <template slot="message">
                                <strong>{{ getErrorTitle }}</strong>
                            </template>
                            <template slot="description">
                                <div>{{ getErrorMessage }}</div>
                            </template>
                        </a-alert>

                        <div class="section-card review-card">
                            <div class="review-icon">
                                <span class="material-icons">check_circle</span>
                            </div>
                            <h3>Ready to submit</h3>
                            <p>
                                Your template will be submitted to Meta for review. This usually takes a few minutes but
                                can take up to 24 hours.
                            </p>
                            <ul class="review-checklist">
                                <li><a-icon type="check" /> Template name: {{ form.name }}</li>
                                <li><a-icon type="check" /> Category: {{ form.category }}</li>
                                <li><a-icon type="check" /> Language: {{ getLanguageLabel }}</li>
                                <li><a-icon type="check" /> Body content added</li>
                                <li v-if="form.buttons.length">
                                    <a-icon type="check" /> {{ form.buttons.length }} button(s) added
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Preview Panel -->
                <TemplatePreview
                    :form="form"
                    :currentStep="currentStep"
                    :media="media"
                    :headerVariables="headerVariables"
                    :bodyVariables="bodyVariables"
                />
            </div>

            <!-- Footer Actions -->
            <div class="form-footer">
                <a-button v-if="currentStep > 0" @click="currentStep--">Previous</a-button>
                <a-button @click="viewTemplates">Discard</a-button>
                <div style="flex: 1"></div>
                <template v-if="editMode && currentStep < 2 && !canProceed">
                    <a-popover :title="'Cannot Proceed'" trigger="hover">
                        <template slot="content">
                            <div style="max-width: 300px;">{{ getChangeReason }}</div>
                        </template>
                        <a-button v-if="currentStep < 2" type="primary" @click="nextStep" :disabled="!canProceed">
                            Next
                        </a-button>
                    </a-popover>
                </template>
                <a-button v-else-if="currentStep < 2" type="primary" @click="nextStep" :disabled="!canProceed">
                    Next
                </a-button>
                <a-button v-if="currentStep === 2" type="primary" @click="submitTemplate" :loading="loading">
                    {{ editMode ? 'Update Template' : 'Submit for Review' }}
                </a-button>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import WABAMenuTypes from "../WABAMenuTypes";
import { sendSocketMessage, socketMessageStream } from "../../../../../utils/socketStream";
import { uploadAsset } from "../../../../../api/assetUpload";
import axios from "axios";
import { WABA_LANGUAGES } from "./wabaLanguages";

// Import components
import TemplateSetup from "./components/steps/TemplateSetup.vue";
import TemplatePreview from "./components/TemplatePreview.vue";
import ButtonEditor from "./components/form-fields/ButtonEditor.vue";
import BodyEditor from "./components/form-fields/BodyEditor.vue";
import HeaderEditor from "./components/form-fields/HeaderEditor.vue";
import CarouselEditor from "./components/editors/CarouselEditor.vue";
import LimitedTimeOfferEditor from "./components/editors/LimitedTimeOfferEditor.vue";
import AuthenticationEditor from "./components/editors/AuthenticationEditor.vue";
import TTLEditor from "./components/editors/TTLEditor.vue";

export default Vue.extend({
    name: "TemplateMessageForm",
    components: {
        TemplateSetup,
        TemplatePreview,
        ButtonEditor,
        BodyEditor,
        HeaderEditor,
        CarouselEditor,
        LimitedTimeOfferEditor,
        AuthenticationEditor,
        TTLEditor,
    },
    props: {
        channel: { required: true },
        changePage: { type: Function, default: () => {} },
        editMode: { type: Boolean, default: false },
        templateToEdit: { type: Object, default: null },
    },
    data() {
        return {
            currentStep: 0,
            loading: false,
            uploading: false,
            subscriber: null,
            media: null,
            asset: null,
            submitError: null,
            currentUploadingCardIndex: undefined,
            headerVariables: [],
            bodyVariables: [],
            originalTemplateData: null, // Edit mode için original template verisi
            originalHeaderMediaHandleId: null, // Edit mode için original header media handle id
            originalCarouselCardMediaHandleIds: [], // Edit mode için original carousel card media handle id'leri
            form: {
                name: "",
                category: "MARKETING",
                templateType: "DEFAULT",
                catalogueSubType: "CATALOGUE",
                language: "en",
                headerFormat: "NONE",
                headerText: "",
                body: "",
                footer: "",
                buttons: [],
                assetId: null,
                assetUrl: null,
                metaAssetHandleId: null,
                metaAssetId: null,
                carouselCards: [],
                messageSendTtlSeconds: null,
                limitedTimeOffer: {
                    text: "",
                    hasExpiration: true,
                },
                authentication: {
                    otpSubType: "COPY_CODE",
                    messageSendTtlSeconds: null,
                    addSecurityRecommendation: true,
                    codeExpirationMinutes: null,
                    zeroTapTermsAccepted: false,
                    supportedApps: [],
                },
            },
            errorInfo: {},
            languages: WABA_LANGUAGES,
            templateTypes: {
                MARKETING: [
                    { id: "DEFAULT", label: "Default", description: "Send messages with media and customised buttons to engage your customers.", disabled: false },
                    { id: "LIMITED_TIME_OFFER", label: "Limited Time Offer", description: "Create urgency with time-sensitive promotions and offer codes.", disabled: false },
                    { id: "CAROUSEL", label: "Carousel", description: "Send a carousel of cards with images and buttons to showcase multiple products or offers.", disabled: false },
                    { id: "CATALOGUE", label: "Product Message", description: "Send messages that drive sales by connecting your product catalogue.", disabled: false },
                    { id: "CALL_PERMISSION", label: "Calling permissions request", description: "Ask customers if you can call them on WhatsApp.", disabled: false },
                    { id: "COUPON_CODE", label: "Coupon Code", description: "Send promotional codes with a copy button for easy redemption.", disabled: false },
                ],
                CATALOGUE_SUBTYPES: [
                    { id: "CATALOGUE", label: "Catalogue Message", description: "Display your full product catalogue with a view catalog button.", disabled: false },
                    { id: "MULTI_PRODUCT", label: "Multi-Product Message", description: "Show up to 30 products from your catalogue in a single message.", disabled: true, disabledReason: "Currently not supported. This template type requires a product catalogue integration." },
                    { id: "SINGLE_PRODUCT", label: "Single-Product Message", description: "Highlight a single product with details and call-to-action button.", disabled: true, disabledReason: "Currently not supported. This template type requires a product catalogue integration." },
                    { id: "PRODUCT_CARD_CAROUSEL", label: "Product Card Carousel", description: "Display up to 10 product cards in a carousel view.", disabled: true, disabledReason: "Currently not supported. This template type requires a product catalogue integration." },
                ],
                UTILITY: [
                    { id: "DEFAULT", label: "Default", description: "Send messages about an existing order or account." },
                ],
                AUTHENTICATION: [
                    { id: "OTP", label: "One-time passcode", description: "Send codes to verify a transaction or login." },
                ],
                AUTHENTICATION_SUBTYPES: [
                    { id: "COPY_CODE", label: "Copy Code", description: "User manually copies the code (standard OTP)." },
                    { id: "ONE_TAP", label: "One Tap", description: "Auto-fill code with one tap on supported Android devices." },
                    { id: "ZERO_TAP", label: "Zero Tap", description: "Auto-fill code automatically without user interaction (requires user consent)." },
                ],
            },
        };
    },
    computed: {
        hasProductButton() {
            if (this.form.templateType === 'CATALOGUE') return true;
            return this.form.buttons.some(btn => btn.type === 'SPM' || btn.type === 'MPM');
        },
        availableButtonTypes() {
            const category = this.form.category;
            const templateType = this.form.templateType;
            const catalogueSubType = this.form.catalogueSubType;
            
            if (category === 'MARKETING') {
                if (templateType === 'CAROUSEL') return [];
                
                if (templateType === 'CATALOGUE') {
                    if (catalogueSubType === 'CATALOGUE') {
                        return [{ value: 'CATALOG', label: 'View Catalog', icon: 'shopping', category: 'cta' }];
                    } else if (catalogueSubType === 'SINGLE_PRODUCT') {
                        return [{ value: 'SPM', label: 'View Product', icon: 'shopping', category: 'cta' }];
                    } else if (catalogueSubType === 'MULTI_PRODUCT') {
                        return [{ value: 'MPM', label: 'View Products', icon: 'shopping-cart', category: 'cta' }];
                    } else if (catalogueSubType === 'PRODUCT_CARD_CAROUSEL') {
                        return [{ value: 'SPM', label: 'View', icon: 'shopping', category: 'cta' }];
                    }
                    return [];
                }
                
                if (templateType === 'LIMITED_TIME_OFFER') {
                    return [
                        { value: 'QUICK_REPLY', label: 'Quick Reply', icon: 'message', category: 'quick_reply' },
                        { value: 'URL', label: 'Visit Website', icon: 'link', category: 'cta' },
                        { value: 'PHONE_NUMBER', label: 'Call Phone', icon: 'phone', category: 'cta' },
                        { value: 'COPY_CODE', label: 'Copy Offer Code', icon: 'copy', category: 'cta' },
                    ];
                }
                
                if (templateType === 'COUPON_CODE') {
                    // COUPON_CODE için: 1 COPY_CODE zorunlu (otomatik ekleniyor) + isteğe bağlı diğer buttonlar
                    // COPY_CODE burada yok çünkü otomatik ekleniyor ve manuel eklenemez
                    return [
                        { value: 'QUICK_REPLY', label: 'Quick Reply', icon: 'message', category: 'quick_reply' },
                        { value: 'URL', label: 'Visit Website', icon: 'link', category: 'cta' },
                        { value: 'PHONE_NUMBER', label: 'Call Phone', icon: 'phone', category: 'cta' },
                    ];
                }
                
                if (templateType === 'CALL_PERMISSION') {
                    return [{ value: 'QUICK_REPLY', label: 'Quick Reply', icon: 'message', category: 'quick_reply' }];
                }
                
                return [
                    { value: 'QUICK_REPLY', label: 'Quick Reply', icon: 'message', category: 'quick_reply' },
                    { value: 'URL', label: 'Visit Website', icon: 'link', category: 'cta' },
                    { value: 'PHONE_NUMBER', label: 'Call Phone', icon: 'phone', category: 'cta' },
                    { value: 'COPY_CODE', label: 'Copy Offer Code', icon: 'copy', category: 'cta' },
                ];
            }
            
            if (category === 'UTILITY') {
                if (templateType === 'CALL_PERMISSION') {
                    // Call Permission için button eklenemez - otomatik ekleniyor
                    return [];
                }
                return [
                    { value: 'QUICK_REPLY', label: 'Quick Reply', icon: 'message', category: 'quick_reply' },
                    { value: 'URL', label: 'Visit Website', icon: 'link', category: 'cta' },
                    { value: 'PHONE_NUMBER', label: 'Call Phone', icon: 'phone', category: 'cta' },
                ];
            }
            
            if (category === 'AUTHENTICATION') {
                const otpSubType = this.form.authentication.otpSubType;
                if (otpSubType === 'COPY_CODE') {
                    return [{ value: 'COPY_CODE', label: 'Copy Code', icon: 'copy', category: 'cta' }];
                }
                if (otpSubType === 'ONE_TAP') {
                    return [{ value: 'OTP', label: 'One Tap Autofill', icon: 'mobile', category: 'cta' }];
                }
                if (otpSubType === 'ZERO_TAP') {
                    return [{ value: 'OTP', label: 'Zero Tap Autofill', icon: 'thunderbolt', category: 'cta' }];
                }
                return [];
            }
            
            return [
                { value: 'QUICK_REPLY', label: 'Quick Reply', icon: 'message', category: 'quick_reply' },
                { value: 'URL', label: 'Visit Website', icon: 'link', category: 'cta' },
            ];
        },
        getTemplateTypes() {
            return this.templateTypes[this.form.category] || [];
        },
        getLanguageLabel() {
            const lang = this.languages.find((l) => l.code === this.form.language);
            return lang ? lang.label : this.form.language;
        },
        getTemplateTypeLabel() {
            const types = this.getTemplateTypes;
            const type = types.find((t) => t.id === this.form.templateType);
            return type ? type.label : "Default";
        },
        getCategoryIcon() {
            return "category-" + this.form.category.toLowerCase();
        },
        getCategoryIconName() {
            const icons = {
                MARKETING: "campaign",
                UTILITY: "build",
                AUTHENTICATION: "verified_user",
            };
            return icons[this.form.category] || "campaign";
        },
        getContentDescription() {
            if (this.hasProductButton) {
                return "Add body text for your product message template. Product header is added automatically from your catalogue. Cloud API hosted by Meta will review the template variables and content.";
            }
            if (this.form.templateType === 'LIMITED_TIME_OFFER') {
                return "Add a header and body for your limited-time offer template. Footer is not supported for this template type. Cloud API hosted by Meta will review the template variables and content.";
            }
            if (this.form.templateType === 'CAROUSEL') {
                return "Add a general body text message and carousel cards. Each carousel card has its own header, optional body text, and buttons. The main body message cannot have buttons - only carousel cards can have buttons. Cloud API hosted by Meta will review the template variables and content.";
            }
            return "Add a header, body and footer for your template. Cloud API hosted by Meta will review the template variables and content.";
        },
        canProceed() {
            if (this.currentStep === 0) {
                // Edit mode'da değişiklik yapılmış mı kontrol et
                if (this.editMode) {
                    return this.hasChanges;
                }
                return true;
            }
            if (this.currentStep === 1) {
                const hasBasicFields = this.form.name && this.form.body;
                // Edit mode'da değişiklik kontrolü
                if (this.editMode && hasBasicFields) {
                    return this.hasChanges;
                }
                return hasBasicFields;
            }
            return false;
        },
        hasChanges() {
            if (!this.editMode || !this.originalTemplateData) return true;
            
            const original = this.originalTemplateData;
            
            // TTL değişiklik kontrolü
            if (this.form.messageSendTtlSeconds !== original.message_send_ttl_seconds) return true;
            
            // Body değişiklik kontrolü
            const originalBody = (original.components || []).find(c => c.type === "BODY");
            if (originalBody && originalBody.text !== this.form.body) return true;
            if (!originalBody && this.form.body) return true;
            
            // Footer değişiklik kontrolü
            const originalFooter = (original.components || []).find(c => c.type === "FOOTER");
            const originalFooterText = originalFooter ? originalFooter.text : null;
            if (originalFooterText !== (this.form.footer || null)) return true;
            
            // Header text değişiklik kontrolü
            const originalHeader = (original.components || []).find(c => c.type === "HEADER");
            if (originalHeader && originalHeader.format === "TEXT") {
                if (originalHeader.text !== this.form.headerText) return true;
            }
            
            // Buttons değişiklik kontrolü
            const originalButtons = (original.components || []).find(c => c.type === "BUTTONS");
            const originalButtonsArray = originalButtons ? originalButtons.buttons : [];
            if (JSON.stringify(originalButtonsArray) !== JSON.stringify(this.form.buttons)) return true;
            
            // Carousel değişiklik kontrolü
            if (this.form.templateType === "CAROUSEL") {
                const originalCarousel = (original.components || []).find(c => c.type === "CAROUSEL");
                if (!originalCarousel) return true; // Yeni carousel eklendi
                if (originalCarousel.cards.length !== this.form.carouselCards.length) return true;
                
                // Her card'ı kontrol et
                for (let i = 0; i < this.form.carouselCards.length; i++) {
                    const card = this.form.carouselCards[i];
                    const origCard = originalCarousel.cards[i];
                    if (!origCard) return true;
                    
                    const cardBody = (origCard.components || []).find(c => c.type === "BODY");
                    if (cardBody && cardBody.text !== card.body) return true;
                    if (!cardBody && card.body) return true;
                    
                    const cardButtons = (origCard.components || []).find(c => c.type === "BUTTONS");
                    const origCardButtons = cardButtons ? cardButtons.buttons : [];
                    if (JSON.stringify(origCardButtons) !== JSON.stringify(card.buttons)) return true;
                }
            }
            
            // LTO değişiklik kontrolü
            if (this.form.templateType === "LIMITED_TIME_OFFER") {
                const originalLTO = (original.components || []).find(c => c.type === "LIMITED_TIME_OFFER");
                if (!originalLTO) return true;
                const origLTO = originalLTO.limited_time_offer || {};
                if (origLTO.text !== this.form.limitedTimeOffer.text) return true;
                if (origLTO.has_expiration !== this.form.limitedTimeOffer.hasExpiration) return true;
            }
            
            // Media değişiklik kontrolü
            if (this.hasMediaChanges) return true;
            
            return false;
        },
        hasMediaChanges() {
            if (!this.originalTemplateData) return false;
            
            // Header media değişikliği
            const originalHeaderUrl = _.get(this.originalTemplateData, "mediaPayload.headerAsset.cdnUrl");
            const currentHeaderUrl = this.asset ? this.asset.url : null;
            if (originalHeaderUrl !== currentHeaderUrl) return true;
            
            // Carousel card media değişiklikleri
            if (this.form.templateType === "CAROUSEL" && this.form.carouselCards) {
                const originalCardAssets = _.get(this.originalTemplateData, "mediaPayload.cardAssets", []);
                for (let i = 0; i < this.form.carouselCards.length; i++) {
                    const card = this.form.carouselCards[i];
                    const originalCardUrl = originalCardAssets[i] ? originalCardAssets[i].cdnUrl : null;
                    const currentCardUrl = card.asset ? card.asset.url : null;
                    if (originalCardUrl !== currentCardUrl) return true;
                }
            }
            
            return false;
        },
        getChangeReason() {
            if (!this.editMode || !this.originalTemplateData) return "";
            
            if (!this.hasChanges) {
                return "No changes have been made to the template. Please make at least one change before proceeding.";
            }
            
            return "";
        },
        getErrorTitle() {
            if (!this.submitError) return "";
            var details = this.submitError.details;
            if (details && details.subcode) {
                var key = "messages.waba.templateError_" + details.subcode;
                var translated = this.$t(key);
                if (translated !== key) return translated;
                return details.title || this.$t("waba.templateCreationFailed");
            }
            return this.$t("waba.templateCreationFailed");
        },
        getErrorMessage() {
            if (!this.submitError) return "";
            var details = this.submitError.details;
            if (details && details.subcode) {
                var key = "waba.templateErrorDesc_" + details.subcode;
                var translated = this.$t(key);
                if (translated !== key) return translated;
                return details.message || this.submitError.message;
            }
            return this.submitError.message;
        },
    },
    watch: {
        "form.templateType": function (newType) {
            this.cleanInvalidButtons();
            if (newType === 'CATALOGUE') {
                this.form.catalogueSubType = 'CATALOGUE';
                this.ensureCatalogueButton();
            }
            if (newType === 'LIMITED_TIME_OFFER' && this.form.buttons.length === 0) {
                // LTO için default button URL olmalı
                this.handleAddButton({ type: 'URL', text: 'Shop now', url: '', phone_number: '', example: '', urlExample: '' });
            }
            if (newType === 'COUPON_CODE') {
                this.ensureCouponCodeButton();
            }
            if (newType === 'CALL_PERMISSION') {
                this.ensureCallPermissionButton();
            }
        },
        "form.catalogueSubType": function () {
            if (this.form.templateType === 'CATALOGUE') {
                this.ensureCatalogueButton();
            }
        },
        "form.authentication.otpSubType": function () {
            if (this.form.category === 'AUTHENTICATION') {
                this.ensureAuthenticationButton();
            }
        },
        "form.category": function (newCategory) {
            // Meta API kuralı: Edit mode'da ve APPROVED template ise category değiştirilemez
            if (this.editMode && this.templateToEdit && this.templateToEdit.status === "APPROVED") {
                // Category değişikliğini geri al
                this.$nextTick(() => {
                    this.form.category = this.templateToEdit.category || "MARKETING";
                });
                this.$message.warning("Category cannot be changed for approved templates.");
                return;
            }
            
            const types = this.templateTypes[newCategory];
            if (types && types.length > 0) {
                this.form.templateType = types[0].id;
            }
            this.cleanInvalidButtons();
            if (newCategory === 'AUTHENTICATION') {
                this.ensureAuthenticationButton();
            }
        },
        "form.body": function() {
            // Body değiştiğinde variables'ı güncelle
            this.findBodyVariables();
        },
        "form.buttons": {
            handler: function(newButtons) {
                const hasProductButton = newButtons.some(btn => btn.type === 'SPM' || btn.type === 'MPM' || btn.type === 'CATALOG');
                if (hasProductButton && this.form.headerFormat !== 'PRODUCT') {
                    this.form.headerFormat = 'PRODUCT';
                } else if (!hasProductButton && this.form.headerFormat === 'PRODUCT') {
                    this.form.headerFormat = 'NONE';
                }
                
                // COUPON_CODE template için sadece 1 COPY_CODE button kontrolü
                if (this.form.templateType === 'COUPON_CODE') {
                    const copyCodeButtons = newButtons.filter(btn => btn.type === 'COPY_CODE');
                    if (copyCodeButtons.length > 1) {
                        // İlk COPY_CODE'u tut, diğerlerini kaldır
                        const firstIndex = newButtons.findIndex(btn => btn.type === 'COPY_CODE');
                        this.form.buttons = newButtons.filter((btn, index) => {
                            return btn.type !== 'COPY_CODE' || index === firstIndex;
                        });
                    } else if (copyCodeButtons.length === 0) {
                        // COPY_CODE yoksa ekle
                        this.ensureCouponCodeButton();
                    } else if (copyCodeButtons.length === 1) {
                        // COUPON_CODE template için COPY_CODE button text her zaman "Copy code" olmalı
                        const copyCodeBtn = copyCodeButtons[0];
                        if (copyCodeBtn.text !== 'Copy code') {
                            copyCodeBtn.text = 'Copy code';
                        }
                    }
                }
                
                // LTO template için button sıralama ve kontrol
                if (this.form.templateType === 'LIMITED_TIME_OFFER') {
                    // Birden fazla COPY_CODE button kontrolü
                    const copyCodeButtons = newButtons.filter(btn => btn.type === 'COPY_CODE');
                    if (copyCodeButtons.length > 1) {
                        // İlk COPY_CODE'u tut, diğerlerini kaldır
                        const firstIndex = newButtons.findIndex(btn => btn.type === 'COPY_CODE');
                        this.form.buttons = newButtons.filter((btn, index) => {
                            return btn.type !== 'COPY_CODE' || index === firstIndex;
                        });
                        return; // Watcher tekrar çalışacak
                    }
                    
                    // Button sıralamasını düzelt: COPY_CODE (index 0), URL (index 1), diğerleri
                    const copyCode = newButtons.filter(btn => btn.type === 'COPY_CODE');
                    const url = newButtons.filter(btn => btn.type === 'URL');
                    const others = newButtons.filter(btn => btn.type !== 'COPY_CODE' && btn.type !== 'URL');
                    
                    const correctOrder = [...copyCode, ...url, ...others];
                    const isOrderCorrect = JSON.stringify(newButtons) === JSON.stringify(correctOrder);
                    
                    if (!isOrderCorrect) {
                        this.form.buttons = correctOrder;
                    }
                }
            },
            deep: true
        },
    },
    mounted() {
        // Edit mode için template verilerini doldur
        if (this.editMode && this.templateToEdit) {
            this.loadTemplateForEdit(this.templateToEdit);
        }
        
        if (this.form.category === 'AUTHENTICATION') {
            this.ensureAuthenticationButton();
        }
        
        if (this.form.templateType === 'COUPON_CODE') {
            this.ensureCouponCodeButton();
        }
        
        if (this.form.templateType === 'CALL_PERMISSION') {
            this.ensureCallPermissionButton();
        }
        
        this.subscriber = socketMessageStream.subscribe((message) => {
            if (message.cmd === "whatsapp_create_template_message_response") {
                this.loading = false;
                if (message.data && message.data.channelId === this.channel._id) {
                    if (message.data.error) {
                        this.submitError = {
                            message: message.data.error,
                            details: message.data.errorDetails || null,
                        };
                    } else {
                        this.submitError = null;
                        this.$message.success("Template başarıyla oluşturuldu ve review'e gönderildi!");
                        this.changePage(WABAMenuTypes.MENU_TYPES.TEMPLATES);
                    }
                }
            }
            if (message.cmd === "whatsapp_update_template_message_response") {
                this.loading = false;
                if (message.data && message.data.channelId === this.channel._id) {
                    if (message.data.error) {
                        this.submitError = {
                            message: message.data.error,
                            details: message.data.errorDetails || null,
                        };
                    } else {
                        this.submitError = null;
                        this.$message.success("Template başarıyla güncellendi!");
                        this.changePage(WABAMenuTypes.MENU_TYPES.TEMPLATES);
                    }
                }
            }
            if (message.cmd === "whatsapp_upload_response") {
                if (message.data && message.data.channelId === this.channel._id) {
                    this.$message.destroy(); // Tüm loading mesajlarını kapat
                    this.uploading = false;
                    
                    // Handle-based upload (template creation için)
                    if (message.data.media && message.data.media.handle) {
                        console.log("whatsapp_upload_response received:", message.data.media);
                        
                        if (this.currentUploadingCardIndex !== undefined) {
                            const cardIndex = this.currentUploadingCardIndex;
                            const card = this.form.carouselCards[cardIndex];
                            
                            console.log("Setting carousel card meta info:", {
                                cardIndex,
                                handle: message.data.media.handle,
                                id: message.data.media.id,
                                mediaId: message.data.media.mediaId
                            });
                            
                            this.$set(this.form.carouselCards[cardIndex], 'metaAssetHandleId', message.data.media.handle);
                            this.$set(this.form.carouselCards[cardIndex], 'metaAssetId', message.data.media.id || message.data.media.mediaId);
                            
                            // assetId ve assetUrl zaten upload sırasında set edilmişti
                            
                            this.$set(this.form.carouselCards[cardIndex], 'uploading', false);
                            this.currentUploadingCardIndex = undefined;
                        } else {
                            this.form.metaAssetHandleId = message.data.media.handle;
                            this.form.metaAssetId = message.data.media.id;
                            // assetId ve assetUrl kaydedelim (normal header için)
                            if (this.asset) {
                                this.form.assetId = this.asset.id;
                                this.form.assetUrl = this.asset.url;
                            }
                        }
                        this.$message.success("Media uploaded successfully");
                    } else if (message.data.error) {
                        this.$message.error("Media upload failed: " + message.data.error);
                        if (this.currentUploadingCardIndex !== undefined) {
                            const cardIndex = this.currentUploadingCardIndex;
                            this.$set(this.form.carouselCards[cardIndex], 'media', null);
                            this.$set(this.form.carouselCards[cardIndex], 'asset', null);
                            this.$set(this.form.carouselCards[cardIndex], 'uploading', false);
                            this.currentUploadingCardIndex = undefined;
                        } else {
                            this.media = null;
                            this.asset = null;
                        }
                    }
                }
            }
        });
    },
    beforeDestroy() {
        if (this.subscriber) {
            this.subscriber.unsubscribe();
        }
    },
    methods: {
        updateCategory(category) {
            this.form.category = category;
        },
        updateTemplateType(typeId) {
            this.form.templateType = typeId;
        },
        updateCatalogueSubType(subType) {
            this.form.catalogueSubType = subType;
        },
        updateAuthSubType(subType) {
            this.form.authentication.otpSubType = subType;
        },
        viewTemplates() {
            this.changePage(WABAMenuTypes.MENU_TYPES.TEMPLATES);
        },
        validateName() {
            const name = this.form.name;
            if (!name) {
                this.errorInfo.name = "Template name is required";
            } else if (!/^[a-z0-9_]+$/.test(name)) {
                this.errorInfo.name = "Only lowercase letters, numbers, and underscores are allowed";
            } else if (name.length > 512) {
                this.errorInfo.name = "Template name must be 512 characters or less";
            } else {
                this.errorInfo.name = null;
            }
        },
        nextStep() {
            if (this.currentStep === 1) {
                this.validateName();
                if (!this.form.body) {
                    this.errorInfo.body = "Body content is required";
                } else {
                    this.errorInfo.body = null;
                }
                
                if (this.form.templateType === 'LIMITED_TIME_OFFER') {
                    if (this.form.buttons.length === 0) {
                        this.$message.error("Limited Time Offer requires at least one button");
                        return;
                    }
                    // URL button zorunlu
                    const hasUrlButton = this.form.buttons.some(btn => btn.type === 'URL');
                    if (!hasUrlButton) {
                        this.$message.error("Limited Time Offer requires a URL button");
                        return;
                    }
                }
                
                if (this.form.templateType === 'CAROUSEL') {
                    // Her carousel card'ının en az bir butona sahip olması gerekiyor
                    if (this.form.carouselCards.length === 0) {
                        this.$message.error("Carousel template requires at least one card");
                        return;
                    }
                    
                    for (let i = 0; i < this.form.carouselCards.length; i++) {
                        const card = this.form.carouselCards[i];
                        if (!card.buttons || card.buttons.length === 0) {
                            this.$message.error(`Card ${i + 1} must have at least one button`);
                            return;
                        }
                        
                        // Card'ın media'sı var mı kontrol et
                        if (!card.metaAssetHandleId) {
                            this.$message.error(`Card ${i + 1} must have a header image or video`);
                            return;
                        }
                    }
                }
                
                if (this.errorInfo.name || this.errorInfo.body) {
                    return;
                }
            }
            this.currentStep++;
        },
        addVariable() {
            const currentVars = (this.form.body.match(/\{\{\d+\}\}/g) || []).length;
            const nextVar = currentVars + 1;
            this.form.body += "{{" + nextVar + "}}";
            this.findBodyVariables();
        },
        insertFormat(char) {
            this.form.body += char + "text" + char;
        },
        findBodyVariables() {
            var regex = /\{\{\d+\}\}/g;
            var matches = this.$_.uniq(this.form.body.match(regex) || []);
            var variableHash = this.$_.keyBy(this.bodyVariables, "variable");
            this.bodyVariables = matches.map((variable) => {
                return {
                    variable: variable,
                    example: this.$_.get(variableHash, [variable, "example"], ""),
                };
            });
        },
        findHeaderVariables() {
            var regex = /\{\{\d+\}\}/g;
            var matches = this.$_.uniq((this.form.headerText || "").match(regex) || []);
            var variableHash = this.$_.keyBy(this.headerVariables, "variable");
            this.headerVariables = matches.map((variable) => {
                return {
                    variable: variable,
                    example: this.$_.get(variableHash, [variable, "example"], ""),
                };
            });
        },
        handleHeaderTextUpdate(value) {
            this.form.headerText = value;
            this.findHeaderVariables();
        },
        updateBodyVariableExample({ index, value }) {
            this.$set(this.bodyVariables[index], 'example', value);
        },
        updateHeaderVariableExample({ index, value }) {
            this.$set(this.headerVariables[index], 'example', value);
        },
        handleAddButton(button) {
            // LIMITED_TIME_OFFER için button sıralaması önemli
            // COPY_CODE index 0'da, URL index 1'de olmalı
            if (this.form.templateType === 'LIMITED_TIME_OFFER') {
                const hasCopyCode = this.form.buttons.some(btn => btn.type === 'COPY_CODE');
                const hasUrl = this.form.buttons.some(btn => btn.type === 'URL');
                
                if (button.type === 'COPY_CODE' && !hasCopyCode) {
                    // COPY_CODE her zaman index 0'a ekle
                    this.form.buttons.unshift(button);
                    return;
                } else if (button.type === 'URL' && !hasUrl) {
                    // URL index 1'e ekle
                    if (hasCopyCode) {
                        this.form.buttons.splice(1, 0, button);
                    } else {
                        this.form.buttons.push(button);
                    }
                    return;
                }
            }
            
            this.form.buttons.push(button);
        },
        handleRemoveButton(index) {
            const button = this.form.buttons[index];
            
            // LIMITED_TIME_OFFER için URL button silinemez
            if (this.form.templateType === 'LIMITED_TIME_OFFER' && button.type === 'URL') {
                const urlButtons = this.form.buttons.filter(btn => btn.type === 'URL');
                if (urlButtons.length === 1) {
                    this.$message.warning("Limited Time Offer requires at least one URL button and it cannot be removed");
                    return;
                }
            }
            
            if (this.form.templateType === 'LIMITED_TIME_OFFER' && this.form.buttons.length === 1) {
                this.$message.warning("Limited Time Offer requires at least one button");
                return;
            }
            
            // COUPON_CODE template için COPY_CODE button silinemez
            if (this.form.templateType === 'COUPON_CODE') {
                if (button.type === 'COPY_CODE') {
                    this.$message.warning("Copy Code button is required for Coupon Code templates and cannot be removed");
                    return;
                }
            }
            
            this.form.buttons.splice(index, 1);
        },
        ensureCatalogueButton() {
            if (this.form.templateType === 'CATALOGUE') {
                let buttonType = 'CATALOG';
                let buttonText = 'View catalog';
                
                if (this.form.catalogueSubType === 'CATALOGUE') {
                    buttonType = 'CATALOG';
                    buttonText = 'View catalog';
                } else if (this.form.catalogueSubType === 'SINGLE_PRODUCT') {
                    buttonType = 'SPM';
                    buttonText = 'View';
                } else if (this.form.catalogueSubType === 'MULTI_PRODUCT') {
                    buttonType = 'MPM';
                    buttonText = 'View items';
                } else if (this.form.catalogueSubType === 'PRODUCT_CARD_CAROUSEL') {
                    buttonType = 'SPM';
                    buttonText = 'View';
                }
                
                this.form.buttons = [{
                    type: buttonType,
                    text: buttonText,
                    url: "",
                    phone_number: "",
                    example: "",
                    urlExample: "",
                }];
                this.form.headerFormat = 'PRODUCT';
            }
        },
        ensureCouponCodeButton() {
            if (this.form.templateType === 'COUPON_CODE') {
                // Tüm COPY_CODE buttonları kaldır
                this.form.buttons = this.form.buttons.filter(btn => btn.type !== 'COPY_CODE');
                
                // Tek bir COPY_CODE button ekle (başa ekle)
                this.form.buttons.unshift({
                    type: 'COPY_CODE',
                    text: 'Copy code',
                    url: "",
                    phone_number: "",
                    example: "",
                    urlExample: "",
                });
            }
        },
        ensureCallPermissionButton() {
            if (this.form.templateType === 'CALL_PERMISSION') {
                // Call Permission için button frontend'den gönderilmez
                // Meta API otomatik call_permission_request component ekler
                this.form.buttons = [];
            }
        },
        ensureAuthenticationButton() {
            if (this.form.category === 'AUTHENTICATION') {
                const otpSubType = this.form.authentication.otpSubType;
                let buttonType = 'COPY_CODE';
                let buttonText = 'Copy code';
                
                if (otpSubType === 'ONE_TAP' || otpSubType === 'ZERO_TAP') {
                    buttonType = 'OTP';
                    buttonText = 'Autofill';
                }
                
                this.form.buttons = [{
                    type: buttonType,
                    text: buttonText,
                    url: "",
                    phone_number: "",
                    example: "",
                    urlExample: "",
                }];
            }
        },
        cleanInvalidButtons() {
            const availableTypes = this.availableButtonTypes.map(bt => bt.value);
            this.form.buttons = this.form.buttons.filter(btn => availableTypes.includes(btn.type));
            
            if (this.form.templateType === 'LIMITED_TIME_OFFER' && this.form.buttons.length === 0) {
                // LTO için default button URL olmalı
                this.handleAddButton({ type: 'URL', text: 'Shop now', url: '', phone_number: '', example: '', urlExample: '' });
            }
            
            if (this.form.templateType === 'COUPON_CODE') {
                this.ensureCouponCodeButton();
            }
        },
        addCarouselCard() {
            const newCard = {
                headerFormat: "IMAGE",
                assetId: null,
                assetUrl: null,
                metaAssetHandleId: null,
                metaAssetId: null,
                body: "",
                bodyVariables: [],
                buttons: [
                    {
                        type: "URL",
                        text: "",
                        url: "",
                        phone_number: "",
                        example: [],
                        urlExample: ""
                    }
                ],
                media: null,
                asset: null,
                uploading: false,
            };
            this.form.carouselCards.push(newCard);
        },
        removeCarouselCard(index) {
            this.form.carouselCards.splice(index, 1);
        },
        addCarouselCardButton({ cardIndex, buttonType }) {
            const button = { 
                type: buttonType, 
                text: "",
                url: "",
                phone_number: "",
                example: [],
                urlExample: ""
            };
            this.form.carouselCards[cardIndex].buttons.push(button);
        },
        removeCarouselCardButton({ cardIndex, buttonIndex }) {
            const card = this.form.carouselCards[cardIndex];
            if (card.buttons.length === 1) {
                this.$message.warning("Each carousel card must have at least one button");
                return;
            }
            this.form.carouselCards[cardIndex].buttons.splice(buttonIndex, 1);
        },
        moveCarouselCardUp(cardIndex) {
            if (cardIndex === 0) return;
            const cards = [...this.form.carouselCards];
            const temp = cards[cardIndex];
            cards[cardIndex] = cards[cardIndex - 1];
            cards[cardIndex - 1] = temp;
            this.form.carouselCards = cards;
        },
        moveCarouselCardDown(cardIndex) {
            if (cardIndex === this.form.carouselCards.length - 1) return;
            const cards = [...this.form.carouselCards];
            const temp = cards[cardIndex];
            cards[cardIndex] = cards[cardIndex + 1];
            cards[cardIndex + 1] = temp;
            this.form.carouselCards = cards;
        },
        updateCarouselCardBody({ cardIndex, value }) {
            this.$set(this.form.carouselCards[cardIndex], 'body', value);
            this.findCarouselCardVariables(cardIndex);
        },
        addCarouselCardVariable(cardIndex) {
            const card = this.form.carouselCards[cardIndex];
            const currentVars = ((card.body || '').match(/\{\{\d+\}\}/g) || []).length;
            const nextVar = currentVars + 1;
            const newBody = (card.body || '') + '{{' + nextVar + '}}';
            this.$set(this.form.carouselCards[cardIndex], 'body', newBody);
            this.findCarouselCardVariables(cardIndex);
        },
        updateCarouselCardVariableExample({ cardIndex, variableIndex, value }) {
            if (!this.form.carouselCards[cardIndex].bodyVariables) {
                this.$set(this.form.carouselCards[cardIndex], 'bodyVariables', []);
            }
            this.$set(this.form.carouselCards[cardIndex].bodyVariables[variableIndex], 'example', value);
        },
        findCarouselCardVariables(cardIndex) {
            const card = this.form.carouselCards[cardIndex];
            const regex = /\{\{\d+\}\}/g;
            const matches = this.$_.uniq((card.body || '').match(regex) || []);
            const variableHash = this.$_.keyBy(card.bodyVariables || [], 'variable');
            const variables = matches.map((variable) => {
                return {
                    variable: variable,
                    example: this.$_.get(variableHash, [variable, 'example'], ''),
                };
            });
            this.$set(this.form.carouselCards[cardIndex], 'bodyVariables', variables);
        },
        async handleCarouselCardFileUpload({ event, cardIndex }) {
            const file = event.target.files[0];
            if (!file) return;

            const mb = file.size / 1024 / 1024;
            const format = this.form.carouselCards[cardIndex].headerFormat;

            if (format === "IMAGE" && mb > 5) {
                this.$message.error("Image must be less than 5 MB");
                return;
            }
            if (format === "VIDEO" && mb > 16) {
                this.$message.error("Video must be less than 16 MB");
                return;
            }

            this.$message.loading("Uploading file...", 0);

            try {
                const path = this.channel.siteId + "/whatsapp-business/" + this.channel._id + "/assets/";
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "meta-waba-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                const _asset = uploadData.data.asset;
                const assetObj = {
                    id: _asset.id,
                    url: _asset.v2Format && _asset.cdnUrl
                        ? _asset.cdnUrl
                        : "https://static.supsis.live/" + _asset.path + _asset.id + "." + _asset.ext,
                    filename: file.name,
                    type: file.type,
                };

                this.$set(this.form.carouselCards[cardIndex], 'asset', assetObj);
                this.$set(this.form.carouselCards[cardIndex], 'assetId', assetObj.id);
                this.$set(this.form.carouselCards[cardIndex], 'assetUrl', assetObj.url);
                this.$set(this.form.carouselCards[cardIndex], 'media', {
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                });

                this.$message.destroy();
                this.$message.loading("Uploading to Meta...", 0);
                this.$set(this.form.carouselCards[cardIndex], 'uploading', true);
                this.currentUploadingCardIndex = cardIndex;
                
                sendSocketMessage("whatsapp upload-file", {
                    asset: assetObj,
                    siteId: this.channel.siteId,
                    channelId: this.channel._id,
                    channelType: this.channel.type,
                });

            } catch (error) {
                this.$message.destroy();
                this.$message.error("File upload failed");
                console.error(error);
            }
        },
        removeCarouselCardMedia(cardIndex) {
            // Media'yı kaldır ama original handle id'yi koru (silindiğinde template'in default media'sı kullanılacak)
            this.$set(this.form.carouselCards[cardIndex], 'media', null);
            this.$set(this.form.carouselCards[cardIndex], 'asset', null);
            this.$set(this.form.carouselCards[cardIndex], 'assetId', null);
            this.$set(this.form.carouselCards[cardIndex], 'assetUrl', null);
            
            const card = this.form.carouselCards[cardIndex];
            const originalHandleId = this.originalCarouselCardMediaHandleIds && this.originalCarouselCardMediaHandleIds[cardIndex];
            
            // Media silindiğinde original handle id'yi kullan (template'in default media'sı kullanılacak)
            if (originalHandleId) {
                // Original handle id'ye geri dön (template'in default media'sı kullanılacak)
                this.$set(this.form.carouselCards[cardIndex], 'metaAssetHandleId', originalHandleId);
            } else {
                // Original handle id yoksa null yap
                this.$set(this.form.carouselCards[cardIndex], 'metaAssetHandleId', null);
            }
            this.$set(this.form.carouselCards[cardIndex], 'metaAssetId', null);
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            const mb = file.size / 1024 / 1024;
            const format = this.form.headerFormat;

            if (format === "IMAGE" && mb > 5) {
                this.$message.error("Image must be less than 5 MB");
                return;
            }
            if (format === "VIDEO" && mb > 16) {
                this.$message.error("Video must be less than 16 MB");
                return;
            }
            if (format === "DOCUMENT" && mb > 100) {
                this.$message.error("Document must be less than 100 MB");
                return;
            }

            this.$message.loading("Uploading file...", 0);

            try {
                const path = this.channel.siteId + "/whatsapp-business/" + this.channel._id + "/assets/";
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "meta-waba-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                const _asset = uploadData.data.asset;
                this.asset = {
                    id: _asset.id,
                    url: _asset.v2Format && _asset.cdnUrl
                        ? _asset.cdnUrl
                        : "https://static.supsis.live/" + _asset.path + _asset.id + "." + _asset.ext,
                    filename: file.name,
                    type: file.type,
                };

                this.media = {
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                };

                this.$message.destroy();
                this.$message.loading("Uploading to Meta...", 0);
                this.uploading = true;
                sendSocketMessage("whatsapp upload-file", {
                    asset: this.asset,
                    siteId: this.channel.siteId,
                    channelId: this.channel._id,
                    channelType: this.channel.type,
                });
            } catch (error) {
                this.$message.destroy();
                this.$message.error("File upload failed");
                console.error(error);
            }
        },
        removeMedia() {
            // Media'yı kaldır ama original handle id'yi koru (silindiğinde template'in default media'sı kullanılacak)
            this.media = null;
            this.asset = null;
            this.form.assetId = null;
            this.form.assetUrl = null;
            // Media silindiğinde original handle id'yi kullan (template'in default media'sı kullanılacak)
            if (this.originalHeaderMediaHandleId) {
                // Original handle id'ye geri dön (template'in default media'sı kullanılacak)
                this.form.metaAssetHandleId = this.originalHeaderMediaHandleId;
            } else {
                // Original handle id yoksa null yap
                this.form.metaAssetHandleId = null;
            }
            this.form.metaAssetId = null;
        },
        submitTemplate() {
            // Carousel template için validasyon
            if (this.form.templateType === 'CAROUSEL') {
                for (let i = 0; i < this.form.carouselCards.length; i++) {
                    const card = this.form.carouselCards[i];
                    if (!card.buttons || card.buttons.length === 0) {
                        this.$message.error(`Card ${i + 1} must have at least one button`);
                        return;
                    }
                    if (!card.metaAssetHandleId) {
                        this.$message.error(`Card ${i + 1} must have a header image or video`);
                        return;
                    }
                }
            }
            
            this.loading = true;
            this.submitError = null;

            // Carousel template için header ve footer gönderilemez (Meta API kısıtlaması)
            var header = null;
            if (this.form.templateType !== 'CAROUSEL' && this.form.headerFormat !== "NONE") {
                header = { format: this.form.headerFormat };
                if (this.form.headerFormat === "TEXT") {
                    header.text = this.form.headerText;
                    if (this.headerVariables.length > 0) {
                        var headerExamples = this.headerVariables.map(v => v.example || "sample");
                        header.example = { header_text: headerExamples };
                    }
                } else if (this.form.headerFormat !== "NONE") {
                    // Edit mode'da media yönetimi
                    if (this.editMode) {
                        // Eğer media değişmemişse (yeni yüklenmemişse ve silinmemişse) original handle id kullan
                        if (!this.form.metaAssetHandleId && this.originalHeaderMediaHandleId) {
                            // Media değişmemiş, original handle id kullan
                            header.example = { header_handle: [this.originalHeaderMediaHandleId] };
                        } else if (this.form.metaAssetHandleId) {
                            // Yeni media yüklenmiş veya silinip original'a dönülmüş, handle id kullan
                            header.example = { header_handle: [this.form.metaAssetHandleId] };
                        }
                    } else {
                        // Create mode - normal akış
                        if (this.form.metaAssetHandleId) {
                            header.example = { header_handle: [this.form.metaAssetHandleId] };
                        }
                    }
                }
            }

            var bodyExample = null;
            if (this.bodyVariables.length > 0) {
                var bodyExamples = this.bodyVariables.map(v => v.example || "sample");
                bodyExample = { body_text: [bodyExamples] };
            }

            var buttons = this.form.buttons
                .filter(b => b.text || b.type === "MPM" || b.type === "SPM" || b.type === "CATALOG" || b.type === "COPY_CODE")
                .map(btn => {
                    if (btn.type === "QUICK_REPLY") return { type: "QUICK_REPLY", text: btn.text };
                    if (btn.type === "URL") {
                        var urlButton = { type: "URL", text: btn.text, url: btn.url };
                        if (this.hasDynamicUrl(btn.url) && btn.urlExample) {
                            urlButton.example = [btn.urlExample];
                        }
                        return urlButton;
                    }
                    if (btn.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: btn.text, phone_number: btn.phone_number };
                    if (btn.type === "COPY_CODE") {
                        // COUPON_CODE template için text kullanılmaz, sadece example
                        return { type: "COPY_CODE", example: btn.example };
                    }
                    if (btn.type === "CATALOG") return { type: "CATALOG", text: btn.text || "View catalog" };
                    if (btn.type === "MPM") return { type: "MPM", text: btn.text || "View items" };
                    if (btn.type === "SPM") return { type: "SPM", text: btn.text || "View" };
                    if (btn.type === "OTP") return { type: "OTP", text: btn.text || "Autofill" };
                    return btn;
                });

            var templateMessage = {
                name: this.form.name,
                // Meta API kuralı: Edit mode'da ve APPROVED template ise category gönderilmez
                category: (this.editMode && this.templateToEdit && this.templateToEdit.status === "APPROVED") 
                    ? undefined 
                    : this.form.category,
                language: this.form.language,
                templateType: this.form.templateType,
                catalogueSubType: this.form.catalogueSubType,
                header: header,
                body: this.form.body,
                bodyExample: bodyExample,
                // Carousel template için footer gönderilemez
                footer: this.form.templateType !== 'CAROUSEL' ? (this.form.footer || null) : null,
                buttons: buttons,
                assetId: this.form.assetId || (this.asset ? this.asset.id : null),
                assetUrl: this.form.assetUrl || (this.asset ? this.asset.url : null),
                metaAssetHandleId: this.form.metaAssetHandleId,
                metaAssetId: this.form.metaAssetId,
                messageSendTtlSeconds: this.form.messageSendTtlSeconds,
            };

            if (this.form.templateType === "CAROUSEL") {
                templateMessage.carouselCards = this.form.carouselCards.map((card, cardIndex) => {
                    var cardButtons = card.buttons
                        .filter(b => b.text)
                        .map(btn => {
                            if (btn.type === "QUICK_REPLY") return { type: "QUICK_REPLY", text: btn.text };
                            if (btn.type === "URL") {
                                var urlButton = { type: "URL", text: btn.text, url: btn.url };
                                if (this.hasDynamicUrl(btn.url) && btn.urlExample) {
                                    urlButton.example = [btn.urlExample];
                                }
                                return urlButton;
                            }
                            if (btn.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: btn.text, phone_number: btn.phone_number };
                            return btn;
                        });
                    
                    // Edit mode'da carousel card media yönetimi
                    let cardMetaAssetHandleId = card.metaAssetHandleId;
                    if (this.editMode) {
                        const originalHandleId = this.originalCarouselCardMediaHandleIds && this.originalCarouselCardMediaHandleIds[cardIndex];
                        // Eğer media değişmemişse (yeni yüklenmemişse ve silinmemişse) original handle id kullan
                        if (!cardMetaAssetHandleId && originalHandleId) {
                            // Media değişmemiş, original handle id kullan
                            cardMetaAssetHandleId = originalHandleId;
                        }
                        // Eğer cardMetaAssetHandleId varsa kullan (yeni yüklenmiş veya silinip original'a dönülmüş)
                    }
                    
                    var cardData = {
                        headerFormat: card.headerFormat,
                        assetId: card.assetId,
                        assetUrl: card.assetUrl,
                        metaAssetHandleId: cardMetaAssetHandleId,
                        metaAssetId: card.metaAssetId,
                        buttons: cardButtons,
                    };
                    
                    // Card body (optional)
                    if (card.body) {
                        cardData.body = card.body;
                        // Card body variables
                        if (card.bodyVariables && card.bodyVariables.length > 0) {
                            cardData.bodyVariables = card.bodyVariables;
                        }
                    }
                    
                    return cardData;
                });
            }

            if (this.form.templateType === "LIMITED_TIME_OFFER") {
                templateMessage.limitedTimeOffer = {
                    text: this.form.limitedTimeOffer.text,
                    hasExpiration: this.form.limitedTimeOffer.hasExpiration,
                };
            }

            if (this.form.category === "AUTHENTICATION") {
                templateMessage.authentication = {
                    otpSubType: this.form.authentication.otpSubType,
                    messageSendTtlSeconds: this.form.authentication.messageSendTtlSeconds,
                    addSecurityRecommendation: this.form.authentication.addSecurityRecommendation,
                    codeExpirationMinutes: this.form.authentication.codeExpirationMinutes,
                    zeroTapTermsAccepted: this.form.authentication.zeroTapTermsAccepted,
                    supportedApps: this.form.authentication.supportedApps.filter(app => app.packageName && app.signatureHash),
                };
            }

            // Edit mode ise update, değilse create
            if (this.editMode && this.templateToEdit) {
                sendSocketMessage("whatsapp update-template-message", {
                    siteId: this.channel.siteId,
                    channelId: this.channel._id,
                    channelType: this.channel.type,
                    templateId: this.templateToEdit.id,
                    currentStatus: this.templateToEdit.status,
                    templateMessage: templateMessage,
                });
            } else {
                sendSocketMessage("whatsapp create-template-message", {
                    siteId: this.channel.siteId,
                    channelId: this.channel._id,
                    channelType: this.channel.type,
                    templateMessage: templateMessage,
                });
            }
        },
        loadTemplateForEdit(template) {
            // Meta formatındaki template'i form'a yükle
            // Bu metod template listesinden gelen Meta formatındaki veriyi parse eder
            this.currentStep = 1; // Direkt edit adımına geç
            
            // Original template verisini sakla (değişiklik kontrolü için)
            this.originalTemplateData = _.cloneDeep({
                name: template.name,
                category: template.category,
                language: template.language,
                components: template.components,
                message_send_ttl_seconds: template.message_send_ttl_seconds,
                mediaPayload: template.mediaPayload,
            });
            
            this.form.name = template.name || "";
            this.form.category = template.category || "MARKETING";
            this.form.language = template.language || "en";
            
            // Components'i parse et
            const components = template.components || [];
            const headerComponent = components.find(c => c.type === "HEADER");
            const bodyComponent = components.find(c => c.type === "BODY");
            const footerComponent = components.find(c => c.type === "FOOTER");
            const buttonsComponent = components.find(c => c.type === "BUTTONS");
            const carouselComponent = components.find(c => c.type === "CAROUSEL");
            const ltoComponent = components.find(c => c.type === "LIMITED_TIME_OFFER");
            
            // Template type detection
            let detectedTemplateType = "DEFAULT";
            if (carouselComponent) {
                detectedTemplateType = "CAROUSEL";
            } else if (ltoComponent) {
                detectedTemplateType = "LIMITED_TIME_OFFER";
            } else if (headerComponent && headerComponent.format === "PRODUCT") {
                detectedTemplateType = "CATALOGUE";
                this.form.catalogueSubType = template.catalogueSubType || "CATALOGUE";
            } else if (components.some(c => c.type === "call_permission_request")) {
                detectedTemplateType = "CALL_PERMISSION";
            } else if (buttonsComponent && buttonsComponent.buttons) {
                const hasCopyCode = buttonsComponent.buttons.some(btn => btn.type === "COPY_CODE");
                if (hasCopyCode && template.category === "MARKETING") {
                    const copyCodeButtons = buttonsComponent.buttons.filter(btn => btn.type === "COPY_CODE");
                    if (copyCodeButtons.length === 1) {
                        detectedTemplateType = "COUPON_CODE";
                    }
                }
            }
            this.form.templateType = detectedTemplateType;
            
            // Header component
            if (headerComponent) {
                this.form.headerFormat = headerComponent.format || "NONE";
                if (headerComponent.format === "TEXT") {
                    this.form.headerText = headerComponent.text || "";
                }
                // Media header için handle ve URL
                if (headerComponent.example && headerComponent.example.header_handle) {
                    const handles = headerComponent.example.header_handle;
                    let originalHandleId = handles[0]; // Meta handle veya URL
                    
                    // Eğer handles[0] bir URL ise (http/https ile başlıyorsa), mediaPayload'dan handle ID al
                    if (originalHandleId && (originalHandleId.startsWith('http://') || originalHandleId.startsWith('https://'))) {
                        // URL ise, mediaPayload'dan handle ID'yi al
                        originalHandleId = _.get(template, "mediaPayload.headerAsset.metaPayload.mediaHandleId") || null;
                    }
                    
                    this.originalHeaderMediaHandleId = originalHandleId; // Original handle id'yi sakla
                    this.form.exampleMedia = originalHandleId;
                    
                    // URL varsa (handles[1]) media preview oluştur, yoksa cdnUrl kullan
                    // Eğer handles[0] URL ise, o URL'yi kullan, değilse handles[1]'i kontrol et
                    let assetUrl = null;
                    if (handles[0] && (handles[0].startsWith('http://') || handles[0].startsWith('https://'))) {
                        assetUrl = handles[0];
                    } else if (handles.length > 1) {
                        assetUrl = handles[1];
                    }
                    
                    if (!assetUrl) {
                        // Template'den media payload'dan URL al
                        assetUrl = _.get(template, "mediaPayload.headerAsset.cdnUrl") ||
                                  _.get(template, "mediaPayload.headerAsset.url") ||
                                  _.get(template, "mediaPayload.headerAsset.path");
                    }
                    
                    if (assetUrl) {
                        this.media = {
                            data: assetUrl,
                            name: 'Template media',
                            type: headerComponent.format === 'IMAGE' ? 'image/jpeg' : 
                                  headerComponent.format === 'VIDEO' ? 'video/mp4' : 'application/pdf',
                        };
                        this.asset = {
                            id: _.get(template, "mediaPayload.headerAsset._id") || _.get(template, "mediaPayload.headerAsset.id") || null,
                            url: assetUrl,
                            filename: 'Template media',
                            type: this.media.type,
                        };
                        // Form'a da kaydet
                        this.form.assetUrl = assetUrl;
                        this.form.assetId = this.asset.id;
                        // Original handle id'yi form'a kaydet (değişiklik yapılmazsa kullanılacak)
                        this.form.metaAssetHandleId = originalHandleId;
                    }
                } else {
                    // Template'den media payload'dan URL al
                    // mediaPayload populate edilmiş olabilir veya direkt cdnUrl içerebilir
                    let headerAssetCdn = _.get(template, "mediaPayload.headerAsset.cdnUrl");
                    if (!headerAssetCdn && template.mediaPayload && template.mediaPayload.headerAsset) {
                        // Eğer populate edilmemişse, headerAsset bir ObjectId olabilir
                        // Bu durumda template listesinden gelen populate edilmiş veriyi kontrol et
                        headerAssetCdn = _.get(template, "mediaPayload.headerAsset.url") || 
                                        _.get(template, "mediaPayload.headerAsset.path");
                    }
                    if (headerAssetCdn) {
                        this.media = {
                            data: headerAssetCdn,
                            name: 'Template media',
                            type: headerComponent.format === 'IMAGE' ? 'image/jpeg' : 
                                  headerComponent.format === 'VIDEO' ? 'video/mp4' : 'application/pdf',
                        };
                        this.asset = {
                            id: _.get(template, "mediaPayload.headerAsset._id") || _.get(template, "mediaPayload.headerAsset.id") || null,
                            url: headerAssetCdn,
                            filename: 'Template media',
                            type: this.media.type,
                        };
                        // Form'a da kaydet
                        this.form.assetUrl = headerAssetCdn;
                        this.form.assetId = this.asset.id;
                    }
                }
            }
            
            // Body component
            if (bodyComponent) {
                this.form.body = bodyComponent.text || "";
            }
            
            // Footer component
            if (footerComponent) {
                this.form.footer = footerComponent.text || "";
            }
            
            // Limited Time Offer component
            if (ltoComponent && ltoComponent.limited_time_offer) {
                this.form.limitedTimeOffer = {
                    text: ltoComponent.limited_time_offer.text || "",
                    hasExpiration: ltoComponent.limited_time_offer.has_expiration !== false,
                };
            }
            
            // Authentication component - body component'inden parse et
            if (this.form.category === "AUTHENTICATION" && bodyComponent) {
                // add_security_recommendation body component'inde
                if (bodyComponent.add_security_recommendation !== undefined) {
                    this.form.authentication.addSecurityRecommendation = bodyComponent.add_security_recommendation;
                }
                
                // message_send_ttl_seconds authentication için
                if (template.message_send_ttl_seconds != null) {
                    this.form.authentication.messageSendTtlSeconds = template.message_send_ttl_seconds;
                }
                
                // OTP subtype button type'ından belirlenir
                if (buttonsComponent && buttonsComponent.buttons && buttonsComponent.buttons.length > 0) {
                    const button = buttonsComponent.buttons[0];
                    if (button.type === "OTP") {
                        // ONE_TAP veya ZERO_TAP - call_permission_request component'inden kontrol et
                        const callPermissionComponent = components.find(c => c.type === "call_permission_request");
                        if (callPermissionComponent) {
                            this.form.authentication.otpSubType = "ZERO_TAP";
                            this.form.authentication.zeroTapTermsAccepted = true;
                            // supportedApps call_permission_request'ten alınabilir
                            if (callPermissionComponent.supported_apps) {
                                this.form.authentication.supportedApps = callPermissionComponent.supported_apps.map(app => ({
                                    packageName: app.package_name,
                                    signatureHash: app.signature_hash,
                                }));
                            }
                        } else {
                            this.form.authentication.otpSubType = "ONE_TAP";
                        }
                    } else {
                        this.form.authentication.otpSubType = "COPY_CODE";
                    }
                }
                
                // codeExpirationMinutes - template'den alınabilir (eğer varsa)
                // Meta API'de code_expiration_minutes genelde template seviyesinde değil, body component'inde olabilir
            }
            
            // TTL (non-authentication templates için)
            if (this.form.category !== "AUTHENTICATION" && template.message_send_ttl_seconds != null) {
                this.form.messageSendTtlSeconds = template.message_send_ttl_seconds;
            }
            
            // Buttons component
            if (buttonsComponent && buttonsComponent.buttons) {
                this.form.buttons = buttonsComponent.buttons.map(btn => ({
                    type: btn.type,
                    text: btn.text || "",
                    url: btn.url || "",
                    phone_number: btn.phone_number || "",
                    example: btn.example || [],
                    urlExample: btn.example && btn.example[0] ? btn.example[0] : "",
                }));
            }
            
            // Carousel component
            if (carouselComponent && carouselComponent.cards) {
                this.form.carouselCards = carouselComponent.cards.map((card, cardIndex) => {
                    const cardComponents = card.components || [];
                    
                    // Card header
                    const cardHeader = cardComponents.find(c => c.type === "HEADER");
                    const headerFormat = cardHeader ? cardHeader.format : "IMAGE";
                    
                    // header_handle: [handle, url] formatında
                    let exampleMedia = null;
                    let assetUrl = null;
                    if (cardHeader && cardHeader.example && cardHeader.example.header_handle) {
                        const handles = cardHeader.example.header_handle;
                        exampleMedia = handles[0]; // Meta handle veya URL
                        
                        // Eğer handles[0] bir URL ise (http/https ile başlıyorsa), mediaPayload'dan handle ID al
                        if (exampleMedia && (exampleMedia.startsWith('http://') || exampleMedia.startsWith('https://'))) {
                            // URL ise, mediaPayload'dan handle ID'yi al
                            exampleMedia = _.get(template, `mediaPayload.cardAssets[${cardIndex}].metaPayload.mediaHandleId`) || null;
                        }
                        
                        // Original handle id'leri sakla
                        if (!this.originalCarouselCardMediaHandleIds) {
                            this.originalCarouselCardMediaHandleIds = [];
                        }
                        this.originalCarouselCardMediaHandleIds[cardIndex] = exampleMedia;
                        
                        // URL varsa (handles[1]) kullan, yoksa cdnUrl'den al
                        // Eğer handles[0] URL ise, o URL'yi kullan, değilse handles[1]'i kontrol et
                        if (handles[0] && (handles[0].startsWith('http://') || handles[0].startsWith('https://'))) {
                            assetUrl = handles[0];
                        } else if (handles.length > 1) {
                            assetUrl = handles[1];
                        }
                        
                        if (!assetUrl) {
                            assetUrl = _.get(template, `mediaPayload.cardAssets[${cardIndex}].cdnUrl`) ||
                                      _.get(template, `mediaPayload.cardAssets[${cardIndex}].url`) ||
                                      _.get(template, `mediaPayload.cardAssets[${cardIndex}].path`);
                        }
                    } else {
                        // Template'den card asset URL'ini al
                        // mediaPayload populate edilmiş olabilir veya direkt cdnUrl içerebilir
                        let cardAssetCdn = _.get(template, `mediaPayload.cardAssets[${cardIndex}].cdnUrl`);
                        if (!cardAssetCdn && template.mediaPayload && template.mediaPayload.cardAssets && template.mediaPayload.cardAssets[cardIndex]) {
                            // Eğer populate edilmemişse, cardAsset bir ObjectId olabilir
                            cardAssetCdn = _.get(template, `mediaPayload.cardAssets[${cardIndex}].url`) || 
                                          _.get(template, `mediaPayload.cardAssets[${cardIndex}].path`);
                        }
                        if (cardAssetCdn) {
                            assetUrl = cardAssetCdn;
                        }
                    }
                    
                    // Card body
                    const cardBody = cardComponents.find(c => c.type === "BODY");
                    const bodyText = cardBody ? cardBody.text : "";
                    const bodyVariables = [];
                    if (cardBody && cardBody.example && cardBody.example.body_text) {
                        const regex = /\{\{\d+\}\}/g;
                        const matches = this.$_.uniq((bodyText || "").match(regex) || []);
                        const examples = cardBody.example.body_text[0] || [];
                        bodyVariables.push(...matches.map((variable, idx) => ({
                            variable: variable,
                            example: examples[idx] || "",
                        })));
                    }
                    
                    // Card buttons
                    const cardButtonsComp = cardComponents.find(c => c.type === "BUTTONS");
                    const cardButtons = cardButtonsComp && cardButtonsComp.buttons 
                        ? cardButtonsComp.buttons.map(btn => ({
                            type: btn.type,
                            text: btn.text || "",
                            url: btn.url || "",
                            phone_number: btn.phone_number || "",
                            example: btn.example || [],
                            urlExample: btn.example && btn.example[0] ? btn.example[0] : "",
                        }))
                        : [];
                    
                    // Media preview - assetUrl varsa media ve asset objelerini oluştur
                    let media = null;
                    let asset = null;
                    if (assetUrl) {
                        media = {
                            data: assetUrl,
                            name: 'Template media',
                            type: headerFormat === 'IMAGE' ? 'image/jpeg' : 
                                  headerFormat === 'VIDEO' ? 'video/mp4' : 'application/pdf',
                        };
                        const cardAssetId = _.get(template, `mediaPayload.cardAssets[${cardIndex}]._id`) || 
                                          _.get(template, `mediaPayload.cardAssets[${cardIndex}].id`);
                        asset = {
                            id: cardAssetId || null,
                            url: assetUrl,
                            filename: 'Template media',
                            type: media.type,
                        };
                    }
                    
                    return {
                        headerFormat: headerFormat,
                        exampleMedia: exampleMedia,
                        body: bodyText,
                        bodyVariables: bodyVariables,
                        buttons: cardButtons,
                        media: media,
                        asset: asset,
                        uploading: false,
                        assetId: asset ? asset.id : null,
                        assetUrl: assetUrl,
                        metaAssetHandleId: exampleMedia || null, // Meta handle varsa kullan (original handle id)
                        metaAssetId: null,
                    };
                });
            }
            
            // Template type'a göre button'ları ayarla
            if (this.form.templateType === 'AUTHENTICATION') {
                this.ensureAuthenticationButton();
            }
            if (this.form.templateType === 'COUPON_CODE') {
                this.ensureCouponCodeButton();
            }
            if (this.form.templateType === 'CALL_PERMISSION') {
                this.ensureCallPermissionButton();
            }
        },
        hasDynamicUrl(url) {
            return url && /\{\{\d+\}\}/.test(url);
        },
    },
});
</script>

<style scoped lang="scss">
@import "./TemplateMessageForm.scss";
</style>
